/**
 * Theme registry/tag API client (spec 006 D1).
 *
 * Frontend calls the main backend proxies:
 *   GET /v1/theme-tags/{ticker}  -> { ticker, themes: TagRead[] }
 *   GET /v1/themes               -> trimmed registry rows
 */

import { API_CONFIG } from '../../config/api';
import { getToken } from '../tokenStorage';

export interface ThemeTag {
  theme_id: number;
  slug: string;
  role: string | null;
  node: string | null;
  derived: boolean;
}

export interface ThemeTagsResponse {
  ticker: string;
  themes: ThemeTag[];
}

export interface ThemeRegistryEntry {
  slug: string;
  display_name: string;
  display_names: Record<string, string>;
  depth: number;
  status: string;
}

/** A break condition, in either shape the registry has used. */
export type ThemeBreakCondition =
  | string
  | {
      id?: string;
      text?: string;
      // 'not_triggered' | 'approaching' | 'fired' | null
      status?: string | null;
    };

/**
 * One theme's investment case.
 *
 * `thesis` is authored by the operator at activation and is not localised: the
 * registry stores one English paragraph. The client shows it verbatim — a
 * paraphrase of someone's investment case is a different investment case — and a
 * localised version would have to be authored, not machine-written.
 */
export interface ThemeDetail {
  slug: string;
  display_name: string;
  display_names: Record<string, string>;
  status: string;
  thesis: string | null;
  thesis_break_conditions: ThemeBreakCondition[];
  conviction_level: number | null;
  conviction_reason: string | null;
}

async function authHeaders(): Promise<Record<string, string>> {
  const token = await getToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/**
 * Fetch the stock's wiki theme tags (explicit + derived).
 * An empty list is a valid 200 (untagged) — never null.
 */
export async function getThemeTags(ticker: string): Promise<ThemeTagsResponse> {
  const res = await fetch(`${API_CONFIG.baseURL}/v1/theme-tags/${encodeURIComponent(ticker)}`, {
    headers: await authHeaders(),
  });
  if (!res.ok) {
    throw new Error(`Theme tags API error: ${res.status}`);
  }
  return res.json();
}

/**
 * Fetch the full theme registry (all locales; client selects).
 * Includes archived themes — they can still carry live tags.
 */
export async function getThemeRegistry(): Promise<ThemeRegistryEntry[]> {
  const res = await fetch(`${API_CONFIG.baseURL}/v1/themes`, {
    headers: await authHeaders(),
  });
  if (!res.ok) {
    throw new Error(`Theme registry API error: ${res.status}`);
  }
  return res.json();
}

/**
 * The registry no longer knows this slug — which includes an archived theme whose
 * tags are still live, the case a row can legitimately open.
 *
 * Its own error type because the distinction is not cosmetic: "the wiki is down"
 * and "this theme is gone" are different sentences to a reader, and a thrown
 * `Error` carrying only a status code makes the caller parse a string to tell them
 * apart.
 */
export class ThemeDetailNotFoundError extends Error {
  readonly status = 404;
  readonly slug: string;

  constructor(slug: string) {
    super(`Theme not in the registry: ${slug}`);
    this.name = 'ThemeDetailNotFoundError';
    this.slug = slug;
  }
}

/**
 * Fetch one theme's thesis and break conditions.
 *
 * Throws `ThemeDetailNotFoundError` for a slug the registry no longer knows, so
 * the caller can say that rather than reporting a transport failure. The wiki
 * answered; it answered "gone".
 */
export async function getThemeDetail(slug: string): Promise<ThemeDetail> {
  const res = await fetch(`${API_CONFIG.baseURL}/v1/themes/${encodeURIComponent(slug)}`, {
    headers: await authHeaders(),
  });
  if (res.status === 404) {
    throw new ThemeDetailNotFoundError(slug);
  }
  if (!res.ok) {
    throw new Error(`Theme detail API error: ${res.status}`);
  }
  return res.json();
}
